import { XHRRequestCanceler } from '../../../../Libs/xhr/XHRRequestCanceler';
import { Constants } from '../../../Constants';
import {
    CauserieDataServiceName,
    ICauserieDataService,
} from '../../../DataServices/CauserieDataService';
import { Lazy } from '../../../Helpers/Lazy';
import { BaseApiDataResponse } from '../../../Models/ApiData';
import {
    CauserieApiDataResponse,
    CauserieListApiDataResponse,
    CauserieOrderBy,
    CauserieResultListApiDataResponse,
} from '../../../Models/Causerie/Causerie';
import { IoC } from '../../ServicesContainer';
import { BaseReduxService } from '../Api/BaseReduxService';
import { I18nServiceName, II18nService } from '../I18n/I18nService';

export interface ICauserieService {
    getCauseries: (
        requestParams: { search: string; order?: CauserieOrderBy },
        cts: XHRRequestCanceler
    ) => Promise<CauserieListApiDataResponse>;

    createCauserie: (
        requestBody: { title: string; description?: string; pdf_id?: string },
        cts: XHRRequestCanceler
    ) => Promise<CauserieApiDataResponse>;

    getCauserieDetails: (
        causerieId: string,
        cts: XHRRequestCanceler
    ) => Promise<CauserieApiDataResponse>;

    updateCauserie: (
        causerie_id: string,
        requestParams: { title: string; description?: string; pdf_id?: string },
        cts: XHRRequestCanceler
    ) => Promise<BaseApiDataResponse>;

    publishCauserie: (causerie_id: string, cts: XHRRequestCanceler) => Promise<BaseApiDataResponse>;

    deleteCauserie: (causerie_id: string, cts: XHRRequestCanceler) => Promise<BaseApiDataResponse>;

    getCauserieResult: (
        causerie_id: string,
        cts: XHRRequestCanceler
    ) => Promise<CauserieResultListApiDataResponse>;
}

class CauserieService extends BaseReduxService implements ICauserieService {
    private readonly causerieDataService: Lazy<ICauserieDataService>;
    private readonly i18n: Lazy<II18nService>;

    constructor() {
        super();
        this.causerieDataService = new Lazy(() =>
            IoC.get<ICauserieDataService>(CauserieDataServiceName)
        );
        this.i18n = new Lazy(() => IoC.get<II18nService>(I18nServiceName));
    }

    public getCauseries = async (
        requestParams: { search: string; order?: CauserieOrderBy },
        cts: XHRRequestCanceler
    ) => {
        try {
            const response: CauserieListApiDataResponse = await this.causerieDataService
                .value()
                .getCauseries(
                    {
                        search: requestParams.search,
                        Count: Constants.DEFAULT_ITEM_COUNT_PER_PAGE,
                        order: requestParams.order,
                    },
                    cts
                );
            return Promise.resolve(response);
        } catch (err) {
            return Promise.reject(err);
        }
    };

    public createCauserie = async (
        requestBody: { title: string; description?: string; pdf_id?: string },
        cts: XHRRequestCanceler
    ): Promise<CauserieApiDataResponse> => {
        try {
            const response = await this.causerieDataService
                .value()
                .createCauserie(requestBody, cts);
            return Promise.resolve(response);
        } catch (err) {
            return Promise.reject(err);
        }
    };

    public getCauserieDetails = async (
        causerieId: string,
        cts: XHRRequestCanceler
    ): Promise<CauserieApiDataResponse> => {
        try {
            const response = await this.causerieDataService
                .value()
                .getCauserieDetails(causerieId, cts);
            return Promise.resolve(response);
        } catch (err) {
            return Promise.reject(err);
        }
    };

    public updateCauserie = async (
        causerie_id: string,
        requestParams: { title: string; description?: string; pdf_id?: string },
        cts: XHRRequestCanceler
    ): Promise<BaseApiDataResponse> => {
        try {
            const response = await this.causerieDataService
                .value()
                .updateCauserie(causerie_id, requestParams, cts);
            return Promise.resolve(response);
        } catch (err) {
            return Promise.reject(err);
        }
    };

    public publishCauserie = async (
        causerie_id: string,
        cts: XHRRequestCanceler
    ): Promise<BaseApiDataResponse> => {
        try {
            const response = await this.causerieDataService
                .value()
                .publishCauserie(causerie_id, cts);
            if (!response.is_success) {
                this.toastApiError(this.i18n.value().getString(`Error_${response.error_code}`));
                return Promise.reject();
            }
            return Promise.resolve(response);
        } catch (err) {
            return Promise.reject(err);
        }
    };

    public deleteCauserie = async (
        causerie_id: string,
        cts: XHRRequestCanceler
    ): Promise<BaseApiDataResponse> => {
        try {
            const response = await this.causerieDataService
                .value()
                .deleteCauserie(causerie_id, cts);
            return Promise.resolve(response);
        } catch (err) {
            return Promise.reject(err);
        }
    };

    public getCauserieResult = async (
        causerie_id: string,
        cts: XHRRequestCanceler
    ): Promise<CauserieResultListApiDataResponse> => {
        try {
            const response = await this.causerieDataService
                .value()
                .getCauserieResult(causerie_id, cts);
            return Promise.resolve(response);
        } catch (err) {
            return Promise.reject(err);
        }
    };
}

const CauserieServiceName = 'CauserieService';
export { CauserieService, CauserieServiceName };
